import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, ExternalLink, Check, X, Ban, Trash2, Save,
  Pencil, TrendingUp, TrendingDown, Minus, Loader2, Truck
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, ReferenceLine } from "recharts";

export default function ProductDetailPage() {
  const { id } = useParams();
  const { t, language } = useI18n();
  // Labels that have no translation key yet: pick by language (nl / en / fr)
  const L = (nl, en, fr) => (language === "nl" ? nl : language === "fr" ? (fr || en) : en);
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddComp, setShowAddComp] = useState(false);
  const [showCosts, setShowCosts] = useState(false);
  const [newComp, setNewComp] = useState({ name: "", url: "", price: 0 });
  const [costs, setCosts] = useState({ purchase_price: 0, shipping_cost: 0, transaction_cost_pct: 2.5, vat_pct: 21 });
  const [defaultShipping, setDefaultShipping] = useState("");
  const [shippingOpen, setShippingOpen] = useState(false);

  const fetchProduct = () => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      if (r.data.costs) setCosts(r.data.costs);
    }).catch(() => navigate("/dashboard/products")).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProduct(); }, [id]);

  const handleAddCompetitor = async () => {
    try {
      await api.post(`/products/${id}/competitors`, { ...newComp, price: parseFloat(newComp.price) });
      toast.success("Concurrent toegevoegd");
      setShowAddComp(false);
      setNewComp({ name: "", url: "", price: 0 });
      fetchProduct();
    } catch { toast.error("Fout bij toevoegen"); }
  };

  const handleDeleteCompetitor = async (compId) => {
    try {
      await api.delete(`/products/${id}/competitors/${compId}`);
      toast.success("Concurrent verwijderd");
      fetchProduct();
    } catch { toast.error("Fout bij verwijderen"); }
  };

  const handleMatchAction = async (compId, action) => {
    try {
      await api.post(`/products/${id}/competitors/${compId}/${action}`);
      toast.success(`Concurrent ${action === "accept" ? "geaccepteerd" : action === "reject" ? "afgewezen" : "geblacklist"}`);
      fetchProduct();
    } catch { toast.error("Fout"); }
  };

  const handleSaveCosts = async () => {
    try {
      await api.put(`/products/${id}/costs`, {
        purchase_price: parseFloat(costs.purchase_price), shipping_cost: parseFloat(costs.shipping_cost),
        transaction_cost_pct: parseFloat(costs.transaction_cost_pct), vat_pct: parseFloat(costs.vat_pct)
      });
      toast.success("Kosten opgeslagen");
      setShowCosts(false);
      fetchProduct();
    } catch { toast.error("Fout bij opslaan"); }
  };

  const handleQuickShipping = async (value) => {
    try {
      await api.put(`/products/${id}/costs`, {
        purchase_price: parseFloat(costs.purchase_price) || 0,
        shipping_cost: parseFloat(value) || 0,
        transaction_cost_pct: parseFloat(costs.transaction_cost_pct) || 2.5,
        vat_pct: parseFloat(costs.vat_pct) || 21
      });
      toast.success(L("Verzendkosten opgeslagen", "Shipping costs saved", "Frais de port enregistrés"));
      setShippingOpen(false);
      fetchProduct();
    } catch { toast.error("Fout"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;
  if (!product) return null;

  // Sort competitors: own shop first (matching user's shop URLs), then by price
  const allCompetitors = product.competitors || [];
  const competitors = [...allCompetitors].sort((a, b) => {
    const aIsOwn = a.name?.toLowerCase().includes("cd-keys") || a.url?.includes(product.shop_id);
    const bIsOwn = b.name?.toLowerCase().includes("cd-keys") || b.url?.includes(product.shop_id);
    if (aIsOwn && !bIsOwn) return -1;
    if (!aIsOwn && bIsOwn) return 1;
    return (a.price || 999) - (b.price || 999);
  });
  const priceHistory = product.price_history || [];

  // Build chart data with clear lines per competitor
  const chartData = [];
  const dates = [...new Set(priceHistory.map((h) => h.recorded_at?.slice(0, 10)))].sort();
  dates.forEach((date) => {
    const point = { date: date?.slice(5) };
    point["Jouw prijs"] = product.price;
    priceHistory.filter((h) => h.recorded_at?.slice(0, 10) === date).forEach((h) => {
      const comp = competitors.find((c) => c.id === h.competitor_id);
      if (comp) point[comp.name] = h.price;
    });
    chartData.push(point);
  });

  const chartColors = ["#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4"];
  const compNames = [...new Set(priceHistory.map((h) => {
    const c = competitors.find((c) => c.id === h.competitor_id);
    return c?.name;
  }).filter(Boolean))];

  // Margin calculation
  const cost = product.costs;
  let margin = null, profit = null, totalCost = null;
  if (cost?.purchase_price) {
    totalCost = cost.purchase_price + (cost.shipping_cost || 0) + (product.price * (cost.transaction_cost_pct || 0) / 100);
    profit = product.price - totalCost;
    margin = product.price > 0 ? (profit / product.price * 100) : 0;
  }

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/products")} data-testid="back-to-products">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{product.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-sm text-muted-foreground font-mono-data">{product.sku}</span>
            {product.ean && <Badge variant="outline" className="text-xs font-mono-data">EAN: {product.ean}</Badge>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold font-mono-data">{product.price?.toFixed(2)} EUR</p>
          <Badge variant="outline" className="capitalize">{product.frequency}</Badge>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Cost card */}
        <Card className="bg-card border-border" data-testid="product-cost-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              {t("products.costs")}
              <Dialog open={showCosts} onOpenChange={setShowCosts}>
                <DialogTrigger asChild><Button variant="ghost" size="sm" data-testid="edit-costs-btn"><Pencil className="w-3 h-3 mr-1" />{t("common.edit")}</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("products.costs")} (excl. BTW)</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div><Label>{t("products.purchase_price")} (EUR)</Label><Input type="number" step="0.01" value={costs.purchase_price} onChange={(e) => setCosts({...costs, purchase_price: e.target.value})} className="mt-1" data-testid="cost-purchase-input" /></div>
                    <div><Label>{t("products.shipping_cost")} (EUR)</Label><Input type="number" step="0.01" value={costs.shipping_cost} onChange={(e) => setCosts({...costs, shipping_cost: e.target.value})} className="mt-1" data-testid="cost-shipping-input" /></div>
                    <div><Label>{t("products.transaction_cost")}</Label><Input type="number" step="0.1" value={costs.transaction_cost_pct} onChange={(e) => setCosts({...costs, transaction_cost_pct: e.target.value})} className="mt-1" data-testid="cost-transaction-input" /></div>
                    <div><Label>{t("products.vat")}</Label><Input type="number" step="0.1" value={costs.vat_pct} onChange={(e) => setCosts({...costs, vat_pct: e.target.value})} className="mt-1" data-testid="cost-vat-input" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCosts(false)}>{t("products.cancel")}</Button>
                    <Button onClick={handleSaveCosts} className="bg-blue-600" data-testid="save-costs-btn"><Save className="w-4 h-4 mr-2" />{t("products.save")}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("products.purchase_price")}</span><span className="font-mono-data">{cost?.purchase_price?.toFixed(2) || "0.00"} EUR</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("products.transaction_cost")}</span><span className="font-mono-data">{cost?.transaction_cost_pct || 0}%</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("products.vat")}</span><span className="font-mono-data">{cost?.vat_pct || 0}%</span></div>
          </CardContent>
        </Card>

        {/* Shipping card with quick edit */}
        <Card className="bg-card border-border" data-testid="product-shipping-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> {L("Verzendkosten", "Shipping costs", "Frais de port")}</span>
              <Popover open={shippingOpen} onOpenChange={setShippingOpen}>
                <PopoverTrigger asChild><Button variant="ghost" size="sm"><Pencil className="w-3 h-3 mr-1" />{L("Wijzig", "Edit", "Modifier")}</Button></PopoverTrigger>
                <PopoverContent className="w-56 p-4" align="end">
                  <p className="text-xs text-muted-foreground mb-3">{L("Verzendkosten per eenheid (excl. BTW)", "Shipping cost per unit (excl. VAT)", "Frais de port par unité (HT)")}</p>
                  <div className="flex items-center gap-2 mb-3">
                    <Input type="number" step="0.01" defaultValue={cost?.shipping_cost || 0} className="h-9 font-mono-data" data-testid="quick-shipping-input"
                      onKeyDown={(e) => { if (e.key === "Enter") handleQuickShipping(e.target.value); }}
                      ref={(el) => { if (el) el._ref = el; }}
                    />
                    <span className="text-sm text-muted-foreground">EUR</span>
                  </div>
                  <div className="flex gap-1.5 mb-3 flex-wrap">
                    {[0, 1.50, 2.50, 3.95, 5.95].map((v) => (
                      <Button key={v} variant="outline" size="sm" className="h-7 text-xs font-mono-data" onClick={() => handleQuickShipping(v)}>
                        {v === 0 ? "Gratis" : `${v.toFixed(2)}`}
                      </Button>
                    ))}
                  </div>
                  <Button size="sm" className="w-full bg-blue-600" onClick={(e) => {
                    const input = e.target.closest('[class*="PopoverContent"]')?.querySelector('input');
                    if (input) handleQuickShipping(input.value);
                  }} data-testid="save-shipping-btn"><Check className="w-3.5 h-3.5 mr-1.5" />Opslaan</Button>
                </PopoverContent>
              </Popover>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold font-mono-data">{(cost?.shipping_cost || 0).toFixed(2)} <span className="text-base text-muted-foreground">EUR</span></p>
            <p className="text-xs text-muted-foreground mt-1">{L("Per eenheid (excl. BTW)", "Per unit (excl. VAT)", "Par unité (HT)")}</p>
          </CardContent>
        </Card>

        {/* Stock card */}
        <Card className="bg-card border-border" data-testid="product-stock-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">{t("products.stock")}</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold font-mono-data">{product.stock}</p>
            <p className="text-xs text-muted-foreground mt-1">{L("Eenheden beschikbaar", "Units available", "Unités disponibles")}</p>
          </CardContent>
        </Card>

        {/* Margin card */}
        <Card className="bg-card border-border" data-testid="product-margin-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">{t("products.margin")}</CardTitle></CardHeader>
          <CardContent>
            {margin !== null ? (<>
              <p className={`text-3xl font-bold font-mono-data ${margin >= 0 ? "text-emerald-500" : "text-red-500"}`}>{margin.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-1">{t("products.profit")}: <span className="font-mono-data">{profit.toFixed(2)} EUR</span></p>
            </>) : <p className="text-muted-foreground text-sm">{L("Voer inkoopprijs in", "Enter purchase price", "Saisissez le prix d'achat")}</p>}
          </CardContent>
        </Card>
      </div>

      {/* Price chart - improved with grid, reference line, clear colors */}
      {chartData.length > 1 && (
        <Card className="bg-card border-border" data-testid="price-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Prijsverloop
              <Badge variant="outline" className="text-xs">{chartData.length} dagen</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v} EUR`} domain={['auto', 'auto']} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))", fontSize: "12px" }}
                  formatter={(value, name) => [`${Number(value).toFixed(2)} EUR`, name]}
                />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <ReferenceLine y={product.price} stroke="#2563EB" strokeDasharray="6 3" strokeWidth={2} label={{ value: `Jouw prijs: ${product.price} EUR`, position: "insideTopRight", fill: "#2563EB", fontSize: 11 }} />
                {compNames.slice(0, 6).map((name, i) => (
                  <Line key={name} type="monotone" dataKey={name} stroke={chartColors[i % chartColors.length]} strokeWidth={2} dot={{ r: 3, fill: chartColors[i % chartColors.length] }} activeDot={{ r: 5 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Competitors - improved with clear price columns */}
      <Card className="bg-card border-border overflow-hidden" data-testid="competitors-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">{t("competitors.title")} ({competitors.length})</CardTitle>
          <Dialog open={showAddComp} onOpenChange={setShowAddComp}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-competitor-btn"><Plus className="w-3 h-3 mr-1" />{t("competitors.add")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("competitors.add")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>{t("competitors.name")} *</Label><Input value={newComp.name} onChange={(e) => setNewComp({...newComp, name: e.target.value})} className="mt-1" data-testid="comp-name-input" /></div>
                <div><Label>{t("competitors.url")}</Label><Input value={newComp.url} onChange={(e) => setNewComp({...newComp, url: e.target.value})} className="mt-1" data-testid="comp-url-input" /></div>
                <div><Label>{t("competitors.price")} (EUR)</Label><Input type="number" step="0.01" value={newComp.price} onChange={(e) => setNewComp({...newComp, price: e.target.value})} className="mt-1" data-testid="comp-price-input" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddComp(false)}>{t("products.cancel")}</Button>
                <Button onClick={handleAddCompetitor} className="bg-blue-600" data-testid="save-competitor-btn">{t("products.save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("competitors.name")}</TableHead>
                <TableHead className="text-right">{L("Concurrent prijs", "Competitor price", "Prix concurrent")}</TableHead>
                <TableHead className="text-right">{L("Verschil", "Difference", "Différence")}</TableHead>
                <TableHead>{t("competitors.stock")}</TableHead>
                <TableHead className="text-right">{t("competitors.confidence")}</TableHead>
                <TableHead className="text-right">{t("products.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {competitors.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Geen concurrenten</TableCell></TableRow>
              ) : (
                competitors.map((c) => {
                  const diff = c.price ? c.price - product.price : null;
                  const diffPct = product.price > 0 && diff !== null ? (diff / product.price * 100) : null;
                  return (
                    <TableRow key={c.id} className="table-row-hover" data-testid={`competitor-row-${c.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{c.name}</span>
                          {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary"><ExternalLink className="w-3 h-3" /></a>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {c.price ? (
                          <span className="font-mono-data font-semibold">{c.price.toFixed(2)} <span className="text-xs text-muted-foreground">EUR</span></span>
                        ) : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {diff !== null ? (
                          <div className="flex items-center justify-end gap-1.5">
                            {diff > 0.01 ? (
                              <div className="flex items-center gap-1 text-emerald-500">
                                <TrendingUp className="w-3.5 h-3.5" />
                                <span className="font-mono-data text-sm font-semibold">+{diff.toFixed(2)}</span>
                                <span className="text-xs opacity-70">({diffPct > 0 ? "+" : ""}{diffPct.toFixed(1)}%)</span>
                              </div>
                            ) : diff < -0.01 ? (
                              <div className="flex items-center gap-1 text-red-500">
                                <TrendingDown className="w-3.5 h-3.5" />
                                <span className="font-mono-data text-sm font-semibold">{diff.toFixed(2)}</span>
                                <span className="text-xs opacity-70">({diffPct.toFixed(1)}%)</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Minus className="w-3.5 h-3.5" />
                                <span className="font-mono-data text-sm">gelijk</span>
                              </div>
                            )}
                          </div>
                        ) : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={c.stock === "in_stock" ? "default" : c.stock === "low_stock" ? "secondary" : "destructive"}
                               className={c.stock === "in_stock" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>
                          {c.stock === "in_stock" ? t("common.in_stock") : c.stock === "low_stock" ? t("common.low_stock") : c.stock === "out_of_stock" ? t("common.out_of_stock") : c.stock || "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className={`font-mono-data ${c.match_confidence >= 85 ? "border-emerald-500/30 text-emerald-400" : c.match_confidence >= 70 ? "border-amber-500/30 text-amber-400" : "border-red-500/30 text-red-400"}`}>
                          {c.match_confidence}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          {c.match_status !== "accepted" && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-500" onClick={() => handleMatchAction(c.id, "accept")} title="Accepteer" data-testid={`accept-comp-${c.id}`}><Check className="w-3.5 h-3.5" /></Button>
                          )}
                          {c.match_status !== "rejected" && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-500" onClick={() => handleMatchAction(c.id, "reject")} title="Afwijzen" data-testid={`reject-comp-${c.id}`}><X className="w-3.5 h-3.5" /></Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleMatchAction(c.id, "blacklist")} title="Blacklist" data-testid={`blacklist-comp-${c.id}`}><Ban className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteCompetitor(c.id)} title="Verwijder" data-testid={`delete-comp-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
